import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { decryptFileBytes } from '@/lib/file-encryption';

/**
 * Hook that downloads an encrypted blob from storage, decrypts it client-side,
 * and returns a local object URL for rendering.
 * 
 * For unencrypted media (no fileKey), it just returns the original URL.
 */
export function useDecryptedMedia(
  storagePathOrUrl: string,
  fileKey: string | null | undefined,
  mimeType: string = 'application/octet-stream'
) {
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    // No encryption - use URL as-is
    if (!fileKey) {
      setDecryptedUrl(storagePathOrUrl);
      return;
    }

    let cancelled = false;

    const decryptMedia = async () => {
      setIsDecrypting(true);
      setError(null);
      try {
        // 1. Get a signed URL for the encrypted blob
        let downloadUrl = storagePathOrUrl;
        if (!storagePathOrUrl.startsWith('http')) {
          const { data } = await supabase.storage
            .from('chat-media')
            .createSignedUrl(storagePathOrUrl, 3600);
          if (!data?.signedUrl) throw new Error('Failed to get signed URL');
          downloadUrl = data.signedUrl;
        }

        // 2. Download the encrypted blob
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error('Failed to download encrypted file');
        const encryptedBytes = await response.arrayBuffer();

        if (cancelled) return;

        // 3. Decrypt client-side
        const plainBytes = await decryptFileBytes(encryptedBytes, fileKey);

        if (cancelled) return;

        // 4. Create object URL from decrypted bytes
        const blob = new Blob([plainBytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setDecryptedUrl(url);
      } catch (err: any) {
        console.error('[E2EE Media] Decryption failed:', err);
        if (!cancelled) setError(err.message || 'Decryption failed');
      } finally {
        if (!cancelled) setIsDecrypting(false);
      }
    };

    decryptMedia();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [storagePathOrUrl, fileKey, mimeType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, []);

  return { decryptedUrl, isDecrypting, error };
}
